'use client'

import { useRef, useState } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import { Check } from 'lucide-react'

import { Badge, Button, Separator, Switch } from '@mdxui/primitives'

import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  headerVariants,
  gridContainerVariants,
  gridItemVariants,
  reducedMotionVariants,
} from '../../lib/motion'

interface PricingPlan {
  name: string
  badge: string
  badgeVariant?: 'default' | 'secondary' | 'outline'
  monthlyPrice: string
  /** Yearly price — only used when billing is 'recurring'. */
  yearlyPrice?: string
  /** Sub-label under the price in 'one-time' billing (e.g. "one-time", "per project"). Omit for no label. */
  priceSuffix?: string
  features: string[]
  buttonText: string
  buttonUrl?: string
  isPopular?: boolean
}

export interface PricingPlansProps {
  title?: string
  description?: string
  plans?: PricingPlan[]
  /**
   * Billing model.
   * - `'recurring'` (default): Monthly/Yearly toggle, prices show "Per month"/"Per year".
   * - `'one-time'`: no toggle, a single price (`monthlyPrice`) with optional `priceSuffix`.
   *   Use for pay-once / one-time-outcome services where monthly/yearly makes no sense.
   * @default 'recurring'
   */
  billing?: 'recurring' | 'one-time'
  className?: string
}

const PricingPlans = ({
  title = 'Pricing',
  description = 'Check out our affordable pricing plans.',
  plans = [
    {
      name: 'Starter',
      badge: 'Starter',
      badgeVariant: 'secondary',
      monthlyPrice: '$0',
      yearlyPrice: '$0',
      features: [
        'Up to 3 projects',
        'Basic analytics',
        'Community support',
        'Free updates',
      ],
      buttonText: 'Get Started',
    },
    {
      name: 'Growth',
      badge: 'Growth',
      badgeVariant: 'default',
      monthlyPrice: '$29',
      yearlyPrice: '$249',
      features: ['Everything in Starter', 'Unlimited projects', 'Advanced analytics', 'Priority support'],
      buttonText: 'Start Trial',
      isPopular: true,
    },
    {
      name: 'Scale',
      badge: 'Scale',
      badgeVariant: 'outline',
      monthlyPrice: '$99',
      yearlyPrice: '$899',
      features: [
        'Everything in Growth',
        'Custom integrations',
        'Dedicated support',
        'SLA guarantee',
      ],
      buttonText: 'Get Started',
    },
  ],
  billing = 'recurring',
  className,
}: PricingPlansProps) => {
  const [isYearly, setIsYearly] = useState(false)
  const recurring = billing === 'recurring'
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const headerAnimation = prefersReducedMotion ? reducedMotionVariants : headerVariants
  const itemAnimation = prefersReducedMotion ? reducedMotionVariants : gridItemVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn('py-16 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container mx-auto">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-6 text-center">
          <motion.div variants={headerAnimation}>
            <h2 className="text-pretty text-3xl font-semibold lg:text-4xl">{title}</h2>
            <div className="mt-4"><Markdown size="lg">{description}</Markdown></div>
          </motion.div>
          {recurring && (
            <motion.div className="flex items-center gap-3 text-sm" variants={headerAnimation}>
              Monthly
              <Switch checked={isYearly} onCheckedChange={() => setIsYearly(!isYearly)} />
              Yearly
            </motion.div>
          )}
          <motion.div
            className="mt-4 flex w-full flex-col items-stretch gap-6 md:flex-row md:justify-center"
            variants={gridContainerVariants(0.1, 0.2)}
          >
            {plans.map((plan) => (
              <motion.div
                key={plan.name}
                className={cn(
                  'flex w-full flex-col rounded-[var(--radius)] border border-border p-6 text-left md:flex-1 md:max-w-sm',
                  plan.isPopular ? 'bg-muted' : ''
                )}
                variants={itemAnimation}
              >
                <Badge variant={plan.badgeVariant || 'secondary'} className="mb-8 block w-fit uppercase">
                  {plan.badge}
                </Badge>
                <span className="text-4xl font-semibold">
                  {recurring && isYearly ? plan.yearlyPrice : plan.monthlyPrice}
                </span>
                {(() => {
                  const suffix = recurring
                    ? isYearly
                      ? 'Per year'
                      : 'Per month'
                    : plan.priceSuffix
                  if (!suffix) return null
                  return (
                    <p
                      className={cn(
                        'text-muted-foreground',
                        recurring && plan.monthlyPrice === '$0' ? 'invisible' : ''
                      )}
                    >
                      {suffix}
                    </p>
                  )
                })()}
                <Separator className="my-6" />
                <div className="flex h-full flex-col justify-between gap-20">
                  <ul className="space-y-4 text-muted-foreground">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2">
                        <Check className="size-4 text-primary" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button variant={plan.isPopular ? 'default' : 'outline'} className="w-full" asChild>
                    <a href={plan.buttonUrl || '#'}>{plan.buttonText}</a>
                  </Button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </motion.section>
  )
}

export { PricingPlans }
